import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { apiErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WorkerPicker from "@/components/op/WorkerPicker";
import SearchSelect from "@/components/op/SearchSelect";
import OpReceipt from "@/components/op/OpReceipt";
import OpHeader from "@/components/op/OpHeader";

const PAGE = 10;

export default function Pengambilan() {
  const [worker, setWorker] = useState(null);
  const [items, setItems] = useState([{ ref_no: "", product_id: "", quantity: "" }]);
  const [tools, setTools] = useState([]);
  const [products, setProducts] = useState([]);
  const [bon, setBon] = useState([]);
  const [units, setUnits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [page, setPage] = useState(0);
  const idemRef = useRef(crypto.randomUUID());

  useEffect(() => {
    api.get("/op/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/op/bon-items").then((r) => setBon(r.data)).catch(() => {});
    api.get("/op/units").then((r) => setUnits(r.data)).catch(() => {});
  }, []);

  const setItem = (i, k, v) => setItems((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setTool = (i, k, v) => setTools((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const addItem = () => { setItems((a) => [...a, { ref_no: "", product_id: "", quantity: "" }]); setPage(Math.floor(items.length / PAGE)); };
  const addTool = (bid) => { const b = bon.find((x) => x.id === bid); const raw = b?.default_unit || "PCS"; const unit = units.some((u) => u.code === raw) ? raw : "PCS"; setTools((a) => [...a, { bon_item_id: bid, quantity: "", unit }]); setPage(Math.floor((items.length + tools.length) / PAGE)); };

  // Paginasi gabungan: barang lalu alat, maks 10 baris/halaman
  const combined = [
    ...items.map((_, i) => ({ kind: "item", i })),
    ...tools.map((_, i) => ({ kind: "tool", i })),
  ];
  const totalPages = Math.max(1, Math.ceil(combined.length / PAGE));
  const curPage = Math.min(page, totalPages - 1);
  const slice = combined.slice(curPage * PAGE, curPage * PAGE + PAGE);
  const pageItems = slice.filter((r) => r.kind === "item").map((r) => r.i);
  const pageTools = slice.filter((r) => r.kind === "tool").map((r) => r.i);

  const save = async (print) => {
    if (!worker) return toast.error("Pilih pengepul dulu");
    const vItems = items.filter((x) => x.product_id && Number(x.quantity) > 0)
      .map((x) => ({ ref_no: x.ref_no || null, product_id: x.product_id, quantity: Number(x.quantity) }));
    const vTools = tools.filter((x) => x.bon_item_id && Number(x.quantity) > 0)
      .map((x) => ({ bon_item_id: x.bon_item_id, quantity: Number(x.quantity), unit: x.unit }));
    if (!vItems.length && !vTools.length) return toast.error("Isi minimal satu barang atau alat");
    setSaving(true);
    try {
      const { data } = await api.post("/op/pengambilan", {
        worker_id: worker.worker_id, items: vItems, tools: vTools, idempotency_key: idemRef.current,
      });
      toast.success(`Tersimpan ke Ledger · ${data.op_no}`);
      setReceipt(data);
      idemRef.current = crypto.randomUUID();
      setItems([{ ref_no: "", product_id: "", quantity: "" }]); setTools([]); setWorker(null); setPage(0);
      if (print) setTimeout(() => window.print(), 400);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const Pager = () => totalPages <= 1 ? null : (
    <div className="flex items-center justify-between rounded-full bg-slate-100 px-2 py-1" data-testid="op-pager">
      <button type="button" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} className="p-1.5 rounded-full disabled:opacity-30 hover:bg-white" data-testid="op-page-prev"><ChevronLeft className="w-4 h-4" /></button>
      <span className="text-xs font-semibold text-slate-600" data-testid="op-page-indicator">Halaman {curPage + 1} / {totalPages}</span>
      <button type="button" disabled={curPage >= totalPages - 1} onClick={() => setPage(curPage + 1)} className="p-1.5 rounded-full disabled:opacity-30 hover:bg-white" data-testid="op-page-next"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div className="w-full pb-10" data-testid="op-pengambilan">
      <OpHeader title="PENGAMBILAN" />
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5">
        <WorkerPicker value={worker} onChange={setWorker} />

        <Pager />

        <div>
          <div className="flex items-center justify-between mb-2"><Label>Rincian Potongan</Label></div>
          <div className="space-y-2">
            {pageItems.map((i) => {
              const it = items[i];
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`op-item-row-${i}`}>
                  <div className="col-span-3"><Input placeholder="No. Potongan" value={it.ref_no} onChange={(e) => setItem(i, "ref_no", e.target.value)} className="h-10" data-testid={`op-item-refno-${i}`} /></div>
                  <div className="col-span-6">
                    <SearchSelect value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}
                      options={products.map((p) => ({ value: p.product_id, label: p.product_name }))}
                      placeholder="Pilih / cari barang" testId={`op-item-product-${i}`} />
                  </div>
                  <div className="col-span-2"><Input type="number" inputMode="numeric" placeholder="Jml" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="h-10" data-testid={`op-item-qty-${i}`} /></div>
                  <div className="col-span-1"><button onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="p-2 text-rose-500" data-testid={`op-item-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                </div>
              );
            })}
            {pageItems.length === 0 && <div className="text-xs text-slate-400">Tidak ada barang di halaman ini.</div>}
          </div>
          <Button variant="outline" size="sm" className="mt-2 rounded-full" onClick={addItem} data-testid="op-add-item"><Plus className="w-4 h-4 mr-1" />Tambah Barang</Button>
        </div>

        <div>
          <Label>Bon Alat Jahit</Label>
          <div className="space-y-2 mt-2">
            {pageTools.map((i) => {
              const tl = tools[i];
              const b = bon.find((x) => x.id === tl.bon_item_id);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`op-tool-row-${i}`}>
                  <div className="col-span-6 text-sm font-medium text-slate-700 pb-2 truncate">{b?.item_name || "-"}</div>
                  <div className="col-span-3"><Input type="number" inputMode="decimal" step="any" placeholder="Jml" value={tl.quantity} onChange={(e) => setTool(i, "quantity", e.target.value)} className="h-10" data-testid={`op-tool-qty-${i}`} /></div>
                  <div className="col-span-2">
                    <Select value={tl.unit} onValueChange={(v) => setTool(i, "unit", v)}>
                      <SelectTrigger className="h-10" data-testid={`op-tool-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{units.map((u) => <SelectItem key={u.code} value={u.code}>{u.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1"><button onClick={() => setTools((a) => a.filter((_, j) => j !== i))} className="p-2 text-rose-500" data-testid={`op-tool-del-${i}`}><Trash2 className="w-4 h-4" /></button></div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 w-64">
            <SearchSelect value="" onValueChange={addTool}
              options={bon.map((b) => ({ value: b.id, label: b.item_name }))}
              placeholder="+ Tambah / cari alat" testId="op-add-tool" className="h-9 rounded-full" />
          </div>
        </div>

        <Pager />

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-xs text-slate-500">Total baris: <b className="text-slate-900">{combined.length}</b> ({items.length} barang · {tools.length} alat)</div>
          <div className="flex gap-2">
            <Button disabled={saving} onClick={() => save(false)} className="rounded-full bg-[#0071E3]" data-testid="op-save"><Save className="w-4 h-4 mr-1.5" />Simpan</Button>
            <Button disabled={saving} onClick={() => save(true)} variant="outline" className="rounded-full" data-testid="op-save-print">Simpan & Cetak</Button>
          </div>
        </div>
      </div>
      {receipt && <OpReceipt doc={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
